import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { Dropdown } from '@gasbuddy/react-components';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { noop } from '../../../lib/utils';

export const GET_BRANDS = gql`
  query Brands {
    brands {
      brandId
      name
    }
  }
`;

export default function BrandsDropdown({
  customOptionLabel,
  onChange,
  selectedBrandId,
  showAllBrandsOption,
  showUnbrandedOption,
  ...rest
}) {
  const { t } = useTranslation('brand');
  const { data } = useQuery(GET_BRANDS);
  const { brands } = data || {};

  const brandOptions = useMemo(() => {
    const brandList = (brands || []).reduce((acc, brand) => {
      if (!brand.name || !brand.brandId) {
        return [...acc];
      }
      return [
        ...acc,
        {
          key: brand.brandId,
          label: brand.name,
          value: brand.brandId,
        },
      ];
    }, []);

    const options = [...brandList];

    if (showAllBrandsOption) {
      options.unshift({
        key: 1,
        label: t('allStationBrands', { ns: 'brand' }),
        value: 1,
      });
    }

    if (showUnbrandedOption) {
      options.unshift({
        key: 0,
        label: t('unbranded', { ns: 'brand' }),
        value: 0,
      });
    }

    if (customOptionLabel) {
      options.unshift({
        key: -1,
        label: customOptionLabel,
        value: -1,
      });
    }

    return options;
  }, [brands, customOptionLabel, showAllBrandsOption, showUnbrandedOption, t]);

  return (
    <Dropdown
      id="brand"
      label={t('selectBrand', { ns: 'brand' })}
      options={brandOptions}
      defaultValue={selectedBrandId}
      onChange={onChange}
      searchable
      {...rest}
    />
  );
}

BrandsDropdown.propTypes = {
  /** Custom option to include at the top of the dropdown (will have value of -1) */
  customOptionLabel: PropTypes.string,
  /** Function called when the dropdown option is changed */
  onChange: PropTypes.func,
  /** The default brand ID to appear selected in the dropdown */
  selectedBrandId: PropTypes.number,
  /** Whether or not to include All Brands as an option */
  showAllBrandsOption: PropTypes.bool,
  /** Whether or not to include Unbranded as an option */
  showUnbrandedOption: PropTypes.bool,
};

BrandsDropdown.defaultProps = {
  customOptionLabel: undefined,
  onChange: noop,
  selectedBrandId: undefined,
  showAllBrandsOption: false,
  showUnbrandedOption: false,
};
